import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { updateUserLanguage } from '@/api/user';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppDispatch } from '@/store/hooks';
import { updateUser } from '@/store/userSlice';
import { isEmbedPathname } from '@/utils/urlBase';

import useLocale from './useLocale';
import { useSupportedLanguageCodes } from './useSupportedLanguages';
import { useUrlState } from './useUrlState';

/**
 * Custom hook to detect and apply language from URL parameters
 * Supports URLs like: /dashboard?lang=fr or /jobs?lang=de
 *
 * This hook:
 * - Runs on initial page load and route changes
 * - Checks for 'lang' URL parameter
 * - Validates against dynamically loaded supported languages
 * - Updates the browser and session locale if valid
 * - Persists the authenticated user's personal default_language
 * - Integrates with existing localStorage system
 *
 * Embed routes are excluded: there `?lang=` is the ATS host's UI language, so
 * `EmbedPluginRoute` applies it together with the `?token=` cleanup in a single
 * navigation, and it must not overwrite the user's saved profile language.
 */
const useUrlLanguageDetection = () => {
  const location = useLocation();
  const { setParams } = useUrlState();
  const dispatch = useAppDispatch();
  const { setBrowserLocale } = useLocale();
  const supportedLanguageCodes = useSupportedLanguageCodes();
  const { user } = useCurrentUser();
  // Tracks the lang we've already persisted, so the dispatch below (which
  // mutates `user`) and other re-renders don't re-fire the update in a loop.
  const persistedLangRef = useRef<string | null>(null);

  /* eslint-disable hireport-design/no-useeffect-api-fetch -- bootstrap: persist URL lang param to user profile once */
  useEffect(() => {
    // Skip if supported languages haven't loaded yet
    if (supportedLanguageCodes.length === 0) {
      return;
    }

    if (isEmbedPathname(location.pathname)) {
      return;
    }

    // Parse URL parameters
    const urlParams = new URLSearchParams(location.search);
    const langParam = urlParams.get('lang')?.toLowerCase();

    // Ignore anything that isn't a known, supported language
    if (!langParam || !supportedLanguageCodes.includes(langParam)) {
      return;
    }

    // Apply the locale (idempotent — i18n no-ops if already active)
    setBrowserLocale(langParam);

    // Persist the authenticated user's personal language preference, once per
    // value. The ref guard is what prevents an update loop: the success
    // dispatch changes `user`, which would otherwise re-run this effect.
    if (user && persistedLangRef.current !== langParam) {
      persistedLangRef.current = langParam;
      updateUserLanguage(langParam)
        .then((res) => {
          dispatch(updateUser({ default_language: res.default_language }));
        })
        .catch(() => {
          // The locale is already applied client-side; clear the guard so a
          // later render can retry persisting it.
          persistedLangRef.current = null;
        });
    }

    // Cosmetically strip the lang param from the address bar.
    setParams({ lang: null });
  }, [
    location.pathname,
    location.search,
    setBrowserLocale,
    supportedLanguageCodes,
    user,
    dispatch,
    setParams,
  ]);
  /* eslint-enable hireport-design/no-useeffect-api-fetch -- bootstrap: persist URL lang param to user profile once */
};

export default useUrlLanguageDetection;
